import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import Toast from './Toast';
import storageService from '../services/storageService';
import { Button, Card, Badge } from './UI';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  Bars3Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [toast, setToast] = useState(null);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    category: '',
    inventoryEnabled: false,
    stockQuantity: 0,
    lowStockThreshold: 10
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const isStorageConfigured = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET &&
                               process.env.REACT_APP_FIREBASE_STORAGE_BUCKET !== 'your_storage_bucket_here';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const productsCollection = collection(db, 'products');
      const productsSnapshot = await getDocs(productsCollection);
      const productsList = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesCollection = collection(db, 'categories');
      const categoriesSnapshot = await getDocs(categoriesCollection);
      const categoriesList = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (categoriesList.length === 0) {
        const defaultCategories = ['Appetizers', 'Main Course', 'Desserts', 'Beverages', 'Snacks', 'Sides', 'Other'];
        for (let i = 0; i < defaultCategories.length; i++) {
          await addDoc(collection(db, 'categories'), { name: defaultCategories[i], order: i });
        }
        fetchCategories();
      } else {
        let needsUpdate = false;
        for (let i = 0; i < categoriesList.length; i++) {
          if (categoriesList[i].order === undefined) {
            needsUpdate = true;
            await updateDoc(doc(db, 'categories', categoriesList[i].id), { order: i });
          }
        }

        if (needsUpdate) {
          fetchCategories();
        } else {
          const sortedCategories = categoriesList.sort((a, b) => (a.order || 0) - (b.order || 0));
          setCategories(sortedCategories);
          if (!formData.category && sortedCategories.length > 0) {
            setFormData(prev => ({ ...prev, category: sortedCategories[0].name }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) {
      showToast('Please enter a category name', 'warning');
      return;
    }

    try {
      const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.order || 0)) : -1;
      await addDoc(collection(db, 'categories'), { name: newCategory.trim(), order: maxOrder + 1 });
      setNewCategory('');
      setShowCategoryForm(false);
      fetchCategories();
      showToast('Category added successfully!', 'success');
    } catch (error) {
      console.error('Error adding category:', error);
      showToast('Failed to add category', 'error');
    }
  };

  const updateCategory = async (categoryId, newName) => {
    if (!newName.trim()) {
      showToast('Category name cannot be empty', 'warning');
      return;
    }

    try {
      await updateDoc(doc(db, 'categories', categoryId), { name: newName.trim() });
      setEditingCategory(null);
      fetchCategories();
      showToast('Category updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating category:', error);
      showToast('Failed to update category', 'error');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? Products with this category will need to be reassigned.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'categories', categoryId));
      fetchCategories();
      showToast('Category deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast('Failed to delete category', 'error');
    }
  };

  const handleDragStart = (e, category, index) => {
    setDraggedCategory({ category, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedCategory || draggedCategory.index === dropIndex) {
      setDraggedCategory(null);
      return;
    }

    try {
      const dragIndex = draggedCategory.index;
      const newCategories = [...categories];

      const [removed] = newCategories.splice(dragIndex, 1);
      newCategories.splice(dropIndex, 0, removed);

      for (let i = 0; i < newCategories.length; i++) {
        await updateDoc(doc(db, 'categories', newCategories[i].id), { order: i });
      }

      setDraggedCategory(null);
      fetchCategories();
      showToast('Category order updated!', 'success');
    } catch (error) {
      console.error('Error reordering categories:', error);
      showToast('Failed to update category order', 'error');
      setDraggedCategory(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedCategory(null);
    setDragOverIndex(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        showToast('Please select a valid image file (JPEG, PNG, WebP, or GIF)', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB', 'error');
        return;
      }
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, imageUrl: previewUrl });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let imageUrl = formData.imageUrl;

      if (imageFile) {
        setUploadProgress(0);
        try {
          imageUrl = await storageService.uploadProductImage(imageFile);
          setUploadProgress(100);
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          showToast('Failed to upload image. Product will be saved without image.', 'warning');
          imageUrl = '';
        }
        setUploadProgress(null);
      }

      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        imageUrl: imageUrl,
        category: formData.category,
        inventoryEnabled: formData.inventoryEnabled,
        updatedAt: serverTimestamp()
      };

      if (formData.inventoryEnabled) {
        productData.stockQuantity = parseInt(formData.stockQuantity) || 0;
        productData.lowStockThreshold = parseInt(formData.lowStockThreshold) || 10;
      }

      if (editingProduct) {
        const productRef = doc(db, 'products', editingProduct.id);
        await updateDoc(productRef, productData);

        if (editingProduct.imageUrl && imageFile && imageUrl !== editingProduct.imageUrl) {
          try {
            await storageService.deleteImage(editingProduct.imageUrl);
          } catch (deleteError) {
            console.error('Error deleting old image:', deleteError);
          }
        }

        if (formData.inventoryEnabled && !editingProduct.inventoryEnabled && formData.stockQuantity > 0) {
          await addDoc(collection(db, 'inventory_transactions'), {
            productId: editingProduct.id,
            productName: formData.name,
            type: 'receive',
            quantityChange: parseInt(formData.stockQuantity),
            stockBefore: 0,
            stockAfter: parseInt(formData.stockQuantity),
            reason: 'Initial Stock',
            notes: 'Inventory tracking enabled',
            performedBy: 'Admin',
            timestamp: serverTimestamp(),
            date: new Date().toISOString()
          });
        }

        showToast('Product updated successfully!', 'success');
      } else {
        productData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, 'products'), productData);

        if (formData.inventoryEnabled && formData.stockQuantity > 0) {
          await addDoc(collection(db, 'inventory_transactions'), {
            productId: docRef.id,
            productName: formData.name,
            type: 'receive',
            quantityChange: parseInt(formData.stockQuantity),
            stockBefore: 0,
            stockAfter: parseInt(formData.stockQuantity),
            reason: 'Initial Stock',
            notes: 'Product created with initial stock',
            performedBy: 'Admin',
            timestamp: serverTimestamp(),
            date: new Date().toISOString()
          });
        }

        showToast('Product added successfully!', 'success');
      }

      setFormData({
        name: '',
        description: '',
        price: '',
        imageUrl: '',
        category: categories.length > 0 ? categories[0].name : '',
        inventoryEnabled: false,
        stockQuantity: 0,
        lowStockThreshold: 10
      });
      setImageFile(null);
      setUploadProgress(null);
      setShowForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      showToast('Failed to save product', 'error');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      imageUrl: product.imageUrl || '',
      category: product.category || (categories.length > 0 ? categories[0].name : ''),
      inventoryEnabled: product.inventoryEnabled || false,
      stockQuantity: product.stockQuantity || 0,
      lowStockThreshold: product.lowStockThreshold || 10
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteDoc(doc(db, 'products', productId));
        showToast('Product deleted successfully!', 'success');
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        showToast('Failed to delete product', 'error');
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      imageUrl: '',
      category: categories.length > 0 ? categories[0].name : '',
      inventoryEnabled: false,
      stockQuantity: 0,
      lowStockThreshold: 10
    });
    setImageFile(null);
    setUploadProgress(null);
    setShowForm(false);
    setEditingProduct(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Product Management</h1>
            <p className="text-text-secondary mt-1">Manage your menu items and categories</p>
          </div>
          <Button
            variant={showForm ? "outline" : "primary"}
            size="lg"
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) handleCancel();
            }}
          >
            {showForm ? (
              <>
                <XMarkIcon className="h-5 w-5" />
                Cancel
              </>
            ) : (
              <>
                <PlusIcon className="h-5 w-5" />
                Add New Product
              </>
            )}
          </Button>
        </div>

        {/* Category Management */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-text-primary">Product Categories</h2>
            <Button
              variant={showCategoryForm ? "ghost" : "success"}
              size="sm"
              onClick={() => setShowCategoryForm(!showCategoryForm)}
            >
              {showCategoryForm ? (
                <>
                  <XMarkIcon className="h-4 w-4" />
                  Cancel
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4" />
                  Add Category
                </>
              )}
            </Button>
          </div>

          {showCategoryForm && (
            <div className="mb-6 flex gap-3 p-4 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Enter category name"
                className="flex-1 px-4 py-2 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-success-500 focus:ring-4 focus:ring-success-500/10 transition-all"
                onKeyPress={(e) => e.key === 'Enter' && addCategory()}
              />
              <Button variant="success" onClick={addCategory}>
                Add
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className={`
                  flex items-center gap-3 p-4 rounded-lg border-2 transition-all
                  ${draggedCategory?.index === index ? 'opacity-40 scale-95' : ''}
                  ${dragOverIndex === index ? 'border-primary-500 bg-primary-50 scale-105' : 'border-neutral-200 bg-white'}
                  ${editingCategory !== category.id ? 'cursor-move hover:border-primary-300 hover:shadow-md' : ''}
                `}
                draggable={editingCategory !== category.id}
                onDragStart={(e) => handleDragStart(e, category, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                {editingCategory === category.id ? (
                  <>
                    <Bars3Icon className="h-5 w-5 text-neutral-300" />
                    <input
                      type="text"
                      defaultValue={category.name}
                      className="flex-1 px-3 py-1.5 border-2 border-primary-500 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          updateCategory(category.id, e.target.value);
                        }
                      }}
                      autoFocus
                    />
                    <button
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        updateCategory(category.id, input.value);
                      }}
                      className="p-1.5 text-success-600 hover:bg-success-50 rounded transition-colors"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setEditingCategory(null)}
                      className="p-1.5 text-neutral-600 hover:bg-neutral-100 rounded transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <Bars3Icon className="h-5 w-5 text-neutral-400" />
                    <span className="flex-1 font-semibold text-text-primary">{category.name}</span>
                    <button
                      onClick={() => setEditingCategory(category.id)}
                      className="p-1.5 text-info-600 hover:bg-info-50 rounded transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteCategory(category.id)}
                      className="p-1.5 text-danger-600 hover:bg-danger-50 rounded transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          {categories.length === 0 && !showCategoryForm && (
            <div className="text-center py-12">
              <p className="text-text-muted">No categories yet. Add your first category to get started!</p>
            </div>
          )}
        </Card>

        {/* Product Form */}
        {showForm && (
          <Card className="overflow-hidden border-2 border-primary-200">
            <div className="bg-gradient-primary p-6 text-white">
              <h2 className="text-2xl font-bold">
                {editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}
              </h2>
              <p className="text-white/90 mt-1">
                Fill in the details below to {editingProduct ? 'update' : 'create'} your product
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-8">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary flex items-center gap-2 pb-3 border-b-2 border-neutral-200">
                  📋 Basic Information
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Product Name <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Cheeseburger Deluxe"
                      required
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all"
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Description <span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Describe your product..."
                      required
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Price (MVR) <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      required
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all"
                    />
                    <p className="mt-1.5 text-sm text-text-muted">Inclusive of 8% GST</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Category <span className="text-danger-600">*</span>
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all appearance-none bg-white cursor-pointer"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Product Image */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary flex items-center gap-2 pb-3 border-b-2 border-neutral-200">
                  📸 Product Image
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    {isStorageConfigured && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          id="product-image-upload"
                          className="hidden"
                        />
                        <label
                          htmlFor="product-image-upload"
                          className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-slate-700 to-slate-600 text-white rounded-lg cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all"
                        >
                          <PhotoIcon className="h-6 w-6" />
                          <span className="font-semibold">
                            {imageFile ? 'Change Image' : 'Choose Image File'}
                          </span>
                        </label>

                        {imageFile && (
                          <div className="flex items-center gap-2 p-3 bg-success-50 border-2 border-success-500 rounded-lg">
                            <CheckCircleIcon className="h-5 w-5 text-success-600" />
                            <span className="text-sm text-success-800 font-medium truncate">
                              {imageFile.name}
                            </span>
                          </div>
                        )}

                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-neutral-300"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="px-3 bg-white text-sm text-text-muted font-medium">OR</span>
                          </div>
                        </div>
                      </>
                    )}

                    {!isStorageConfigured && (
                      <div className="flex items-start gap-3 p-4 bg-warning-50 border-l-4 border-warning-500 rounded-lg">
                        <ExclamationTriangleIcon className="h-6 w-6 text-warning-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-warning-800">Firebase Storage not configured</p>
                          <p className="text-sm text-warning-700 mt-1">Please use image URL instead</p>
                        </div>
                      </div>
                    )}

                    <input
                      type="url"
                      name="imageUrl"
                      value={formData.imageUrl}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-4 py-3 border-2 border-dashed border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 focus:border-solid focus:ring-4 focus:ring-primary-500/10 transition-all"
                    />

                    {uploadProgress !== null && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm font-medium text-text-secondary">
                          <span>Uploading...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-primary transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {formData.imageUrl && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-text-primary">Preview</p>
                      <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-neutral-200 bg-neutral-100">
                        <img
                          src={formData.imageUrl}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inventory Management */}
              <div className="space-y-4 p-6 bg-gradient-to-br from-warning-50 to-warning-100/50 rounded-lg border-2 border-warning-200">
                <h3 className="text-lg font-bold text-text-primary flex items-center gap-2 pb-3 border-b-2 border-warning-200">
                  📦 Inventory Management
                </h3>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-text-primary">Enable inventory tracking</p>
                    <p className="text-sm text-text-muted mt-1">Track stock levels for this product</p>
                  </div>
                  <Switch
                    checked={formData.inventoryEnabled}
                    onChange={(checked) => setFormData({ ...formData, inventoryEnabled: checked })}
                    className={`${
                      formData.inventoryEnabled ? 'bg-success-500' : 'bg-neutral-300'
                    } relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-4 focus:ring-success-500/20`}
                  >
                    <span
                      className={`${
                        formData.inventoryEnabled ? 'translate-x-7' : 'translate-x-1'
                      } inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform`}
                    />
                  </Switch>
                </div>

                {formData.inventoryEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t-2 border-warning-200">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Current Stock Quantity <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="stockQuantity"
                        value={formData.stockQuantity}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-success-500 focus:ring-4 focus:ring-success-500/10 transition-all bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Low Stock Alert Threshold <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="lowStockThreshold"
                        value={formData.lowStockThreshold}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="10"
                        required
                        className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:outline-none focus:border-warning-500 focus:ring-4 focus:ring-warning-500/10 transition-all bg-white"
                      />
                      <p className="mt-1.5 text-sm text-text-muted">Alert when stock falls below this level</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex gap-4 pt-6 border-t-2 border-neutral-200">
                <Button type="submit" variant="success" size="lg" fullWidth>
                  {editingProduct ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Update Product
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-5 w-5" />
                      Add Product
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" size="lg" onClick={handleCancel} className="min-w-[120px]">
                  <XMarkIcon className="h-5 w-5" />
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Products List */}
        <Card>
          <h2 className="text-2xl font-bold text-text-primary mb-6">
            All Products ({products.length})
          </h2>

          {products.length === 0 ? (
            <div className="text-center py-16">
              <PhotoIcon className="h-20 w-20 text-neutral-300 mx-auto mb-4" />
              <p className="text-text-muted text-lg">No products available</p>
              <p className="text-text-muted text-sm mt-2">Add your first product to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map(product => {
                const isOutOfStock = product.inventoryEnabled && product.stockQuantity === 0;
                const isLowStock = product.inventoryEnabled && product.stockQuantity <= product.lowStockThreshold && product.stockQuantity > 0;

                return (
                  <Card
                    key={product.id}
                    hover
                    className={`overflow-hidden transition-all ${isOutOfStock ? 'opacity-60' : ''}`}
                  >
                    {/* Product Image */}
                    {product.imageUrl ? (
                      <div className="relative h-48 bg-neutral-100 -m-6 mb-4 overflow-hidden">
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Stock Badge */}
                        {product.inventoryEnabled && (
                          <div className="absolute top-2 right-2">
                            {isOutOfStock ? (
                              <Badge variant="danger">Out of Stock</Badge>
                            ) : isLowStock ? (
                              <Badge variant="warning">Low Stock: {product.stockQuantity}</Badge>
                            ) : (
                              <Badge variant="success">In Stock: {product.stockQuantity}</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="relative h-48 bg-gradient-to-br from-neutral-100 to-neutral-200 -m-6 mb-4 flex items-center justify-center">
                        <PhotoIcon className="h-16 w-16 text-neutral-400" />
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="space-y-2">
                      <h3 className="font-bold text-lg text-text-primary line-clamp-1">
                        {product.name}
                      </h3>
                      <p className="text-sm text-text-muted line-clamp-2">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-2xl font-bold text-primary-600">
                          MVR {product.price.toFixed(2)}
                        </span>
                        <Badge variant="neutral" size="sm">
                          {product.category}
                        </Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-200">
                      <button
                        onClick={() => handleEdit(product)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-info-50 text-info-700 font-medium rounded-lg hover:bg-info-100 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-danger-50 text-danger-700 font-medium rounded-lg hover:bg-danger-100 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ProductManagement;
